import appConfigJson from "./app.config.json";
import featuresConfigJson from "./features.config.json";
import { useFeatureEnabled, useEffectiveFeatures } from "@/lib/features";

/**
 * The frontend's configuration utility - the single place a component reads
 * a static default (locale/currency/date format) or a deployment capability
 * flag, instead of an inline literal ("en-IN", "INR") or an ad-hoc boolean.
 *
 * This is deliberately a thin layer over two different existing mechanisms,
 * not a new one:
 *  - appConfig/hasCapability read config/*.json, bundled at build time (see
 *    those files' own header comments) - static, non-secret, identical for
 *    every tenant on this deployment.
 *  - isModuleEnabled/useModuleEnabled re-export lib/features.ts's existing
 *    per-tenant Feature Toggle Management hooks unchanged - that mechanism
 *    already does exactly what "control module visibility" needs
 *    (GET /features/my, resolved server-side per tenant); this file just
 *    gives it a name consistent with the rest of this module so a page
 *    doesn't have to know which of the two systems a given flag lives in.
 */

export interface AppConfig {
  default_locale: string;
  default_currency: string;
  default_currency_symbol: string;
  default_date_format: string;
  default_datetime_format: string;
}

export interface FeatureFlags {
  biometric_integration: boolean;
  email_notifications: boolean;
  push_notifications: boolean;
  audit_logs: boolean;
  activity_tracking: boolean;
  reports: boolean;
  analytics: boolean;
  ai_features: boolean;
}

export const appConfig: AppConfig = appConfigJson as AppConfig;

const featureFlags: FeatureFlags = (featuresConfigJson as { features: FeatureFlags }).features;

/** A deployment-level capability flag (e.g. "email_notifications") - see
 * this file's header on how this differs from per-tenant module visibility. */
export function hasCapability(key: keyof FeatureFlags): boolean {
  return featureFlags[key] ?? false;
}

/** Re-exported for a single import site - see this file's header. */
export const useModuleEnabled = useFeatureEnabled;
export const useEffectiveModules = useEffectiveFeatures;
